/**
 * Generic paginated fetch that pages through a supabase-js query in stable
 * chunks, accumulating every row until a short page signals the end. This
 * sidesteps the backend's default response-row cap (~1,300) so a user's entire
 * trip list — or the full public trip set — is returned with no hard ceiling.
 *
 * The caller supplies a `fetchPage(from, to)` that MUST append `.range(from, to)`
 * and an explicit deterministic `.order('id', ...)` tiebreaker (on top of any
 * existing ordering) so rows are never skipped or duplicated across pages.
 */

export const PAGE_SIZE = 500;

/**
 * Per-screen page size for UI lists that render to users (photo grids, feeds,
 * etc.). Kept in one place so every paginated screen uses a consistent chunk
 * size. Keep this small enough that the first screen paints fast, but large
 * enough that "Load more" isn't tapped too often.
 */
export const DISPLAY_PAGE_SIZE = 60;

export interface PageResult<T> {
  data: T[] | null;
  error: unknown;
}

export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
): Promise<PageResult<T>> {
  const all: T[] = [];
  let from = 0;

  try {
    for (;;) {
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await fetchPage(from, to);
      if (error) return { data: null, error };
      const page = data ?? [];
      all.push(...page);
      // A short page means we've read past the end of the list.
      if (page.length < PAGE_SIZE) break;
      from = to + 1;
    }
  } catch (err) {
    return { data: null, error: err };
  }

  return { data: all, error: null };
}